import React, { FC, useCallback, useContext, useMemo } from 'react';
import { EOrganizationRole, organizationRoles } from '../../const/roles';
import { CurrentUserContext } from '../../contexts/CurrentUserContext';
import { SelectTable } from '../SelectTable/SelectTable';
import Box from '@mui/system/Box';
import { useTranslation } from 'react-i18next';
import { IBasicOrganization } from '../../resources/models/IOrganization';
import { UUID } from '../../types/uuid';
import { WarningAlert } from '../Alert/WarningAlert';

interface IProps {
  value: null | IBasicOrganization;
  onChange: (organization: null | IBasicOrganization) => void;
  defaultOrganizationId?: UUID | null;
}

export const OrganizationsTable: FC<IProps> = ({ value, onChange, defaultOrganizationId }) => {
  const { t } = useTranslation();
  const { userInfo } = useContext(CurrentUserContext);

  const columns = useMemo(
    () => [
      {
        id: 'name',
        name: t('common.tableHeader.name'),
        format: (val: string) => val,
      },
      {
        id: 'isDefault',
        name: '',
        format: (val: string) => val,
      },
      {
        id: 'role',
        name: t('common.tableHeader.role'),
        format: (val: string) => val,
      },
      // {
      //   id: 'lastSignIn',
      //   name: 'Last Sign In',
      //   format: (val: string) => val,
      // },
    ],
    [t]
  );

  const rows = useMemo(() => {
    const rolesPerOrg = (userInfo?.roles ?? []).reduce((out, role) => {
      if (role.organization_name) {
        if (!(role.organization_name in out)) {
          out[role.organization_name] = [];
        }

        const roleDef = organizationRoles.find(({ id }) => id === role.id);
        if (roleDef) {
          out[role.organization_name].push(roleDef.role);
        }
      }
      return out;
    }, {} as Record<string, string[]>);

    return (userInfo?.organizations ?? []).map((organization) => ({
      id: organization.id,
      cells: [
        organization.name,
        organization.id === defaultOrganizationId ? 'default' : '',
        rolesPerOrg[organization.name]?.includes(EOrganizationRole.Admin)
          ? t('roles.organization.admin')
          : rolesPerOrg[organization.name]?.includes(EOrganizationRole.Member)
          ? t('roles.organization.member')
          : '-',
      ],
    }));
  }, [userInfo]);

  const onRowSelect = useCallback(
    (value: string | null) => {
      onChange(value ? userInfo?.organizations?.find(({ id }) => id === value) ?? null : null);
    },
    [userInfo?.organizations]
  );

  const onClearSelection = useCallback(() => {
    onChange(null);
  }, []);

  if (!userInfo) {
    return null;
  }

  return rows.length > 0 ? (
    <SelectTable
      hasHeader={false}
      rows={rows}
      columns={columns}
      setSelectedRow={onRowSelect}
      selectedRow={value ? value.id : void 0}
      clearSelectedRow={onClearSelection}
    />
  ) : (
    <Box mt={2}>
      <WarningAlert>You have no organizations</WarningAlert>
    </Box>
  );
};
